import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plane, Route, Train, Building2, GraduationCap, HeartPulse, TrendingUp, Sparkles } from 'lucide-react';

/**
 * "Why Kalyani?" briefing modal — pops when a buyer clicks the Kalyani
 * catalyst chip on any nearby-property card. Shows the four big appreciation
 * drivers for the Kalyani-Nadia belt in a scannable timeline layout.
 *
 * Zero data fetches, pure content. Closes on Escape / backdrop / X button.
 */

const MILESTONES = [
  {
    icon: Plane,
    year: '2025 · confirmed',
    title: 'Kalyani International Airport',
    body: 'Newly confirmed greenfield airport site announced by the state, roughly 4 km east of Kalyani town. AAI height-restriction zones (0–4 km strict, 4–15 km graded) already model into the locality report on every nearby plot.',
    tone: 'emerald',
  },
  {
    icon: Route,
    year: 'active',
    title: 'Kalyani Expressway · NH-12',
    body: 'The Nimta → Sodepur → Barrackpore → Halisahar → Kalyani → Krishnanagar corridor is a state-notified high-speed expressway. Any plot along BT Road / NH-12 already inherits the "strong highway connectivity" ROI catalyst.',
    tone: 'blue',
  },
  {
    icon: Train,
    year: '2026-2029 · phased',
    title: 'Pink Line Metro extension',
    body: 'Confirmed section: Baranagar → Dakshineswar → Kamarhati → Panihati → Sodepur → Khardah → Titagarh → Barrackpore. Approved extension: Barrackpore → Naihati → Halisahar → Kanchrapara → Kalyani. Plots within 2 km of any planned station get a CAGR boost in the projected-price graph.',
    tone: 'rose',
  },
  {
    icon: Building2,
    year: 'active',
    title: 'AIIMS Kalyani + IIT Kalyani',
    body: 'A 950-bed AIIMS teaching hospital and the IIT Kharagpur satellite campus are already operational — anchoring a large student, faculty and healthcare rental market year-round.',
    tone: 'amber',
  },
  {
    icon: GraduationCap,
    year: 'active',
    title: 'University belt · high rental demand',
    body: 'Kalyani University, JIS Group, Kalyani Government Engineering College and Kalyani Mahavidyalaya together house tens of thousands of students, driving a strong 1RK / hostel / PG rental demand.',
    tone: 'violet',
  },
  {
    icon: HeartPulse,
    year: 'active',
    title: 'Healthcare + smart-city upgrades',
    body: 'AIIMS Kalyani, Kalyani JNM Hospital, and the Kalyani Smart City programme (Kalyani Municipality, one of 100 in the national mission) mean sanctioned civic upgrades — sewage, water, roads, LED — are continuously happening.',
    tone: 'sky',
  },
];

const TONE = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-800',    dot: 'bg-blue-500' },
  rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-800',    dot: 'bg-rose-500' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-800',   dot: 'bg-amber-500' },
  violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-800',  dot: 'bg-violet-500' },
  sky:     { bg: 'bg-sky-50',     border: 'border-sky-200',     text: 'text-sky-800',     dot: 'bg-sky-500' },
};

export const KalyaniStoryModal = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const modal = (
    <div
      className="fixed inset-0 z-[1250] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-3 sm:p-6"
      onClick={onClose}
      data-testid="kalyani-story-modal"
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header banner */}
        <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800 text-white p-5 sm:p-7">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white inline-flex items-center justify-center"
            aria-label="Close"
            data-testid="kalyani-story-close"
          >
            <X size={15} />
          </button>
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest bg-white/15 rounded-full px-2.5 py-1 mb-3">
            Investment brief
          </div>
          <h2 className="text-2xl sm:text-3xl font-black leading-tight">
            Why Kalyani, Nadia?
          </h2>
          <p className="text-sm text-emerald-100 mt-1.5 leading-relaxed">
            Four state-backed catalysts (airport + expressway + metro + healthcare
            hub) converging on the same 20 km belt — a rare set-up for
            long-hold land appreciation in eastern India.
          </p>
        </div>

        {/* Timeline */}
        <div className="p-5 sm:p-7 space-y-4">
          {MILESTONES.map((m, i) => {
            const Icon = m.icon;
            const t = TONE[m.tone];
            return (
              <div
                key={i}
                className={`relative rounded-xl border ${t.border} ${t.bg} p-4 flex items-start gap-3`}
                data-testid={`kalyani-milestone-${i}`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${t.dot} text-white inline-flex items-center justify-center shadow-md`}>
                  <Icon size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`text-[10px] font-black uppercase tracking-widest ${t.text} mb-0.5`}>
                    {m.year}
                  </div>
                  <div className="text-sm font-black text-slate-900 leading-tight mb-1">
                    {m.title}
                  </div>
                  <p className="text-[12px] text-slate-700 leading-relaxed">
                    {m.body}
                  </p>
                </div>
              </div>
            );
          })}

          {/* Bottom CTA card */}
          <div className="mt-2 rounded-xl border-2 border-dashed border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-4 flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-600 text-white inline-flex items-center justify-center shadow-md">
              <TrendingUp size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-0.5">
                Bottom line
              </div>
              <p className="text-[12.5px] text-slate-800 leading-relaxed">
                Plots near Kalyani today sit at land-bank prices while four
                heavyweight infrastructure catalysts are simultaneously going
                live. Historically, Indian belts that clustered an airport +
                metro + expressway (Devanahalli, Noida Extension, Panvel) all
                delivered <strong className="text-emerald-800">3–5× appreciation over 7 years</strong>.
                Every price / CAGR / rental figure in the locality report on
                these plots already accounts for the milestones above.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default KalyaniStoryModal;
